import React, { useState } from 'react'
import { DollarSign, Percent } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../ui'
import { ModernInput, ModernSelect, ModernButton, ModernTextarea } from '../ui'
import { PriceInput } from '../form'
import { FinancialCharge, FinancialChargeFormData, ChargeType } from '../../types'
import { useFinancialChargeOptions } from '../../hooks/useFinancialChargeOptions'
import { formatCurrency, parseCurrency } from '../../utils/formatters'

interface FinancialChargeFormProps {
 charge?: FinancialCharge | null
 onSubmit: (data: FinancialChargeFormData) => void
 onCancel: () => void
 isSubmitting?: boolean
}

export const FinancialChargeForm: React.FC<FinancialChargeFormProps> = ({
 charge,
 onSubmit,
 onCancel,
 isSubmitting = false
}) => {
 const { options } = useFinancialChargeOptions()
 
 const [formData, setFormData] = useState<FinancialChargeFormData>({
 name: charge?.name || '',
 type: charge?.type || 'other',
 amount: charge?.amount || 0,
 percentage: charge?.percentage || 0,
 isPercentage: charge?.isPercentage || false,
 isRequired: charge?.isRequired || false,
 description: charge?.description || '',
 isActive: charge?.isActive !== undefined ? charge.isActive : true
 })

 const [amountInput, setAmountInput] = useState<string>(
 charge?.amount ? formatCurrency(charge.amount) : ''
 )
 const [percentageInput, setPercentageInput] = useState<string>(
 charge?.percentage ? `${charge.percentage}%` : ''
 )

 const handleInputChange = (field: keyof FinancialChargeFormData, value: any) => {
 setFormData(prev => ({
 ...prev,
 [field]: value
 }))
 }

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault()
 onSubmit(formData)
 }

 const handleAmountChange = (value: string) => {
 setAmountInput(value)
 const numericValue = parseCurrency(value)
 handleInputChange('amount', numericValue)
 }

 const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 // Permite números decimais (ex: 4.5%, 10.25%, etc)
 const value = e.target.value.replace(/[^\d,.]/g, '').replace(',', '.')
 // Remove múltiplos pontos decimais
 const parts = value.split('.')
 const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value
 const numericValue = parseFloat(cleanValue) || 0
 setPercentageInput(cleanValue ? `${cleanValue}%` : '')
 handleInputChange('percentage', numericValue)
 }


 return (
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* Informações do Encargo */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="h-5 w-5"/>
 Dados do Encargo
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Nome do Encargo *
 </label>
 <ModernInput
 value={formData.name}
 onChange={(e) => handleInputChange('name', e.target.value)}
 placeholder="Ex: Taxa de Transferência"
 required
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Tipo *
 </label>
 <ModernSelect
 value={formData.type}
 onChange={(e) => handleInputChange('type', e.target.value as ChargeType)}
 options={[
 { value: '', label: 'Selecione o tipo' },
 ...options.types
 ]}
 placeholder="Selecione o tipo"
 required
 />
 </div>
 </div>

 <div className="flex items-center space-x-4">
 <label className="flex items-center space-x-2">
 <input
 type="checkbox"
 checked={formData.isRequired}
 onChange={(e) => handleInputChange('isRequired', e.target.checked)}
 className="rounded border-border text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">Encargo obrigatório</span>
 </label>
 <label className="flex items-center space-x-2">
 <input
 type="checkbox"
 checked={formData.isActive}
 onChange={(e) => handleInputChange('isActive', e.target.checked)}
 className="rounded border-border text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">Ativo</span>
 </label>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Valor Fixo
 </label>
 <div className="relative">
 <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
 <DollarSign className="h-4 w-4"/>
 </div>
 <PriceInput
 value={formData.isPercentage ? '' : amountInput}
 onChange={handleAmountChange}
 placeholder="R$ 0,00"
 disabled={formData.isPercentage}
 className="pl-10"
 />
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Percentual
 </label>
 <div className="relative">
 <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
 <Percent className="h-4 w-4"/>
 </div>
 <ModernInput
 type="text"
 value={formData.isPercentage ? percentageInput : ''}
 onChange={handlePercentageChange}
 placeholder="0%"
 disabled={!formData.isPercentage}
 className="pl-10"
 />
 </div>
 </div>
 </div>

 <div className="flex items-center space-x-4">
 <label className="flex items-center space-x-2">
 <input
 type="checkbox"
 checked={formData.isPercentage}
 onChange={(e) => {
 handleInputChange('isPercentage', e.target.checked)
 if (e.target.checked) {
 handleInputChange('amount', 0)
 setAmountInput('')
 } else {
 handleInputChange('percentage', 0)
 setPercentageInput('')
 }
 }}
 className="rounded border-border text-accent focus:ring-accent"
 />
 <span className="text-sm text-foreground">Usar percentual em vez de valor fixo</span>
 </label>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Descrição
 </label>
 <ModernTextarea
 value={formData.description}
 onChange={(e) => handleInputChange('description', e.target.value)}
 placeholder="Descrição detalhada do encargo..."
 rows={3}
 />
 </div>
 </CardContent>
 </Card>

 {/* Botões */}
 <div className="flex justify-end gap-3 pt-4 border-t border-border">
 <ModernButton
 type="button"
 variant="outline"
 onClick={onCancel}
 disabled={isSubmitting}
 >
 Cancelar
 </ModernButton>
 <ModernButton
 type="submit"
 loading={isSubmitting}
 disabled={isSubmitting || !formData.name || !formData.type}
 >
 {charge ? 'Atualizar Encargo' : 'Criar Encargo'}
 </ModernButton>
 </div>
 </form>
 )
}
